import { useState, useEffect } from 'react'
import { List, Datagrid, FunctionField, downloadCSV, TextField as RATextField, Pagination, useListContext } from 'react-admin'
import jsonExport from 'jsonexport/dist'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Typography } from '@material-ui/core'
import DateRangeSelector from './components/DateRangeSelector'
import ExpandableMessagePanel from './components/ExpandableMessagePanel'
import LogsSearchFilter from './components/SearchFilter'
import DataExpandablePanel from './components/DataExpandablePanel'
import LogHistogram from './components/LogHistogram'
import * as api from '../data'

const useStyles = makeStyles((theme) => ({
  timeframeContainer: { display: 'flex', alignItems: 'center', margin: theme.spacing(2) },
  listContainer: { 
    padding: theme.spacing(0.5),
    paddingTop: 0,
    marginTop: 0,
    "& .MuiToolbar-regular":{ 
      padding: 0,
      minHeight: 0
    },
    "& .MuiToolbar-gutters":{ 
      padding: 0
    }
  },
  rowNumberCell: { width: 50, fontWeight: 'bold', textAlign: 'center' },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    marginTop: 0,
    paddingTop: 0,
  },
  centerSection: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
  },
  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
  },
}))

const exporter = (logs) => {
  jsonExport(
    logs,
    { headers: ['id', 'timestamp', 'date', 'severity', 'errorCode', 'source', 'process', 'message'] },
    (err, csv) => downloadCSV(csv, 'modality-logs')
  )
}

const formatDate = (timestamp) => (timestamp ? new Date(timestamp).toISOString().replace('Z', '') : '')

// Custom List component to access data for histogram
const LogsListWithHistogram = (props) => {
  const classes = useStyles()
  const [timeframeFilter, setTimeframeFilter] = useState({ date_gte: undefined, date_lte: undefined })
  const [modality, setModality] = useState(null)
  const [histogramTimeRange, setHistogramTimeRange] = useState('7d') // Default to 7 days

  // Extract modalityId from props.filter
  const modalityId = props.filter?.modalityId

  // Fetch modality information
  useEffect(() => {
    const fetchModality = async () => {
      if (modalityId) {
        try {
          const modalityData = await api.modalities.fetch(modalityId)
          setModality(modalityData)
        } catch (error) {
          console.error('Error fetching modality:', error)
        }
      }
    }

    fetchModality()
  }, [modalityId])

  const handleTimeframeChange = (startTime, endTime) => {
    setTimeframeFilter({
      ...(startTime?.startsWith('-') ? { startTime } : startTime ? { date_gte: startTime } : {}),
      ...(endTime && { date_lte: endTime }),
    })
    
    // Update histogram time range based on the timeframe selector
    if (startTime?.startsWith('-')) {
      setHistogramTimeRange(startTime)
    } else if (startTime && endTime) {
      // For custom date ranges, default to 24h
      setHistogramTimeRange('24h')
    }
  }

  const handleHistogramTimeRangeClick = (startTime, endTime) => {
    // Update the timeframe filter when clicking on histogram bars
    setTimeframeFilter({
      date_gte: startTime.toISOString(),
      date_lte: endTime.toISOString(),
    })
  }

  return (
    <div className={classes.listContainer}>
      <Box className={classes.headerContainer}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {modality?.modality || 'Machine'} Logs
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {modality ? `${modality.displayName} - ${modality.manufacturer}` : 'System events and error logs'}
          </Typography>
        </Box>
        
        <Box className={classes.rightSection}>
          <DateRangeSelector onChange={handleTimeframeChange} />
        </Box>
      </Box>
      
      <List
        {...props}
        title=' '
        filters={<LogsSearchFilter />}
        exporter={exporter}
        bulkActionButtons={false}
        filter={{ ...props.filter, ...timeframeFilter }}
        hasCreate={false}
        hasEdit={false}
        hasList={false}
        hasShow={false}
        sort={{ field: 'timestamp', order: 'DESC' }}
        pagination={<Pagination rowsPerPageOptions={[100, 1000, 5000]} />}
      >
        <LogsListContent 
          histogramTimeRange={histogramTimeRange}
          onHistogramTimeRangeClick={handleHistogramTimeRangeClick}
        />
      </List>
    </div>
  )
}

// Separate component to access ListContext
const LogsListContent = ({ histogramTimeRange, onHistogramTimeRangeClick }) => {
  const { data: logs } = useListContext()
  
  // Debug logging to see what data we're getting
  console.log('LogsListContent data:', {
    logs,
    type: typeof logs,
    isArray: Array.isArray(logs),
    length: logs?.length,
    sample: logs?.[0]
  })
  
  // Convert logs object to array if needed
  const logsArray = logs && typeof logs === 'object' && !Array.isArray(logs) 
    ? Object.values(logs) 
    : (Array.isArray(logs) ? logs : [])
  
  console.log('Converted logs array:', {
    logsArray,
    length: logsArray.length,
    sample: logsArray[0]
  })
  
  return (
    <>
      <LogHistogram 
        logs={logsArray} 
        timeRange={histogramTimeRange}
        onTimeRangeClick={onHistogramTimeRangeClick}
      />
      <Datagrid size='small' rowClick={false} expand={<DataExpandablePanel />}>
        <FunctionField label='#' render={(record) => <span>{record.id}</span>} />
        <FunctionField label='Timestamp' render={(record) => formatDate(record.timestamp)} sortBy='timestamp' />
        <RATextField source='severity' />
        <RATextField source='source' />
        <RATextField source='errorCode' />
        <FunctionField label='Message' render={(record) => <ExpandableMessagePanel record={record} />} />
      </Datagrid>
    </>
  )
}

export default LogsListWithHistogram
